import {
    Button,
    createStyles,
    Divider,
    Link,
    Paper,
    TextField,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import React, { ChangeEvent, Component } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router";
import { LinkProps, Link as ReactLink } from "react-router-dom";
import { bindActionCreators } from "redux";
import ReactGA from "react-ga4";
import Constants from "../../models/utility/Constants";
import { LoadState } from "../../models/utility/LoadState";
import LoaderButton from "../shared/LoaderButton";
import SectionBanner from "../shared/SectionBanner";
import { IAppDispatch, IAppState } from "./../../store";
import * as UserStore from "./../../store/UserStore";
import * as AppConfigStore from "../../store/AppConfigStore";
import { RedirectTo } from "../../helpers/RedirectTo";

type LoginProps = UserStore.IUserStoreState &
    AppConfigStore.IAppConfigStoreState &
    typeof UserStore.actionCreators &
    WithStyles<typeof styles>;
interface ILoginState {
    email: string;
    password: string;
}
const WhiteTextTypography = withStyles({
    root: {
        color: "#FFFFFF"
    }
})(Typography) as typeof Typography;

const styles = (theme: Theme) =>
    createStyles({
        button: { marginTop: theme.spacing(3) },
        divider: {
            marginBottom: theme.spacing(4),
            marginTop: theme.spacing(4)
        },
        forgotLink: {
            cursor: "pointer",
            display: "block",
            marginTop: theme.spacing(2)
        },
        form: {
            maxWidth: 650,
            padding: theme.spacing(8),
            width: "100%"
        },
        formFieldTop: {
            marginTop: 0
        },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#231F20"
        },
        input: {
            color: "white"
        },
        floatingLabel: {
            color: "#CCCCCC"
        }
    });

class Login extends Component<LoginProps, ILoginState> {
    constructor(props: LoginProps) {
        super(props);

        Constants.SetDocumentTitle("Login");

        this.props.toggleLogonFailure(false);
        this.state = {
            email: "",
            password: ""
        };
    }

    public componentDidMount() {
        this.props.toggleRedirectToLogin(false);
    }

    public render() {
        if (this.props.user) {
            if (this.props.prevLocation) {
                return <Redirect to={this.props.prevLocation} />;
            } else {
                RedirectTo(
                    Constants.Routes.AuctionList({
                        baseUrl: this.props.salesPageUrl
                    })
                );
            }
        }

        const classes = this.props.classes;

        return (
            <Paper className={classes.paper} elevation={2}>
                <SectionBanner leftSideText="Login" />
                <div className={classes.form}>
                    <form>
                        {this.props.logonFailure ? this.renderFailure() : null}
                        <TextField
                            className={classes.formFieldTop}
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            label="Email"
                            name="email"
                            type="email"
                            autoFocus={true}
                            value={this.state.email}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        />
                        <TextField
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            type="password"
                            label="Password"
                            name="password"
                            value={this.state.password}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        />
                        <div className={classes.button}>
                            <LoaderButton
                                loading={
                                    this.props.authLoadState ===
                                    LoadState.Loading
                                }
                                type="submit"
                                size="large"
                                variant="contained"
                                onClick={this.onSubmit}
                            >
                                Login
                            </LoaderButton>
                        </div>
                        <Link
                            className={classes.forgotLink}
                            component={this.forgotPassLink}
                        >
                            <Typography variant="body1" color="primary">
                                I forgot my password
                            </Typography>
                        </Link>
                    </form>
                    <Divider className={classes.divider} />
                    <WhiteTextTypography component="h5" variant="h5">
                        Don't have an account?
                    </WhiteTextTypography>
                    <Button
                        className={classes.button}
                        component={this.getRegisterLink}
                        size="large"
                        variant="contained"
                    >
                        Register to Bid
                    </Button>
                </div>
            </Paper>
        );
    }

    private renderFailure = () => {
        return (
            <div>
                <Typography align="center" variant="subtitle1" color="error">
                    Unable to login to your account.
                    <br /> If you're having trouble signing in please email{" "}
                    <Link href="mailto:info@clhbid.com" color="inherit">
                        info@clhbid.com
                    </Link>{" "}
                    or call{" "}
                    <Link href="tel:+18662637480" color="inherit">
                        1-866-263-7480
                    </Link>{" "}
                    for assistance.
                </Typography>
            </div>
        );
    };

    private onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value as string;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.setState({ [name]: value } as any);
    };

    private onSubmit = (e: React.MouseEvent) => {
        e.preventDefault();
        ReactGA.event(Constants.GAEvents.Form.Submit);
        ReactGA.event(Constants.GAEvents.Login);
        this.props.loginUser(this.state.email, this.state.password);
    };

    private getRegisterLink = (props: Partial<LinkProps>) => (
        <ReactLink to={Constants.Routes.Register()} {...props} />
    );
    private forgotPassLink = (props: Partial<LinkProps>) => (
        <ReactLink to={Constants.Routes.ForgotPassword()} {...props} />
    );
}

const reduxState = (state: IAppState) => ({
    ...state.userStoreState,
    ...state.appConfigStoreState
});
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(UserStore.actionCreators, dispatch);

export default withStyles(styles)(connect(reduxState, reduxDispatch)(Login));
